import { cn } from "@/lib/utils";

const Bar = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "rounded bg-container-brand-minimal animate-pulse",
      className,
    )}
  />
);

/** The three at-a-glance cells under the title - @see `VenueStatsRow`. */
const StatsRowSkeleton = () => (
  <div className="flex items-stretch">
    {[0, 1, 2].map((cell) => (
      <div
        key={cell}
        // 6px / 21px value / 2px / 16px label / 6px = the real cell's 51px.
        className="flex-1 flex flex-col items-center justify-center gap-0.5 px-1 py-1.5"
      >
        <Bar className="h-[21px] w-12" />
        <Bar className="h-4 w-16" />
      </div>
    ))}
  </div>
);

/** One line of the tab panel: an icon square and two lines of text. */
const PanelRowSkeleton = () => (
  <div className="flex items-start gap-3 px-4 py-3 md:px-0">
    <Bar className="size-10 shrink-0 rounded-lg" />
    <div className="flex-1 flex flex-col gap-1.5 pt-1">
      <Bar className="h-4 w-2/5" />
      <Bar className="h-4 w-4/5" />
    </div>
  </div>
);

/**
 * The venue details page as a skeleton, shown until every section can be drawn
 * at once - @see `useVenueDetailsReveal` in the route.
 *
 * Drawn to the basic venue layout's own measurements (`VenueSheetContent` plus
 * the route's map column): the 290px left column with the 4:3 hero, the stats
 * row and the Contribute button, the tab bar, and the bordered tab panel. The
 * pieces are bars rather than the real components in a loading state - unlike
 * home's skeleton - because none of these sections has a loading state of its
 * own to borrow.
 *
 * A complex venue's page is a different shape, and is never shown behind this:
 * @see the route, which reveals those immediately.
 *
 * Purely decorative: it is `aria-hidden`, and the route announces the wait
 * once for a screen reader. The venue's real markup stays mounted underneath -
 * this covers it rather than replacing it - so the server-rendered page keeps
 * carrying the venue's name and summary for crawlers.
 */
const VenueDetailsSkeleton = () => {
  return (
    <div aria-hidden="true" className="h-full bg-bg-brand-secondary-default">
      <div className="h-full flex overflow-hidden md:grid md:grid-rows-1 md:grid-cols-[1fr_360px] gap-4">
        <div className="flex flex-col min-w-0 h-full overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-0 md:pl-4 md:pb-4">
            {/* Back navigation (mobile) */}
            <div className="flex lg:hidden px-2 shrink-0 py-4">
              <Bar className="h-5 w-28" />
            </div>

            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden gap-4">
              {/* ===== LEFT COLUMN (desktop only) ===== */}
              <div className="hidden lg:flex lg:flex-col lg:w-[290px] lg:shrink-0">
                {/* Back button row - h-14 so its rule lines up with the tabs'. */}
                <div className="h-14 flex items-center border-b border-border-token-brand-minimal shrink-0 -mr-4 pr-4">
                  <Bar className="h-5 w-24" />
                </div>

                <div className="flex-1 flex flex-col gap-4 overflow-hidden mt-4">
                  {/* Hero. mb-4 matches the room the real one leaves for the
                      action cluster straddling its bottom edge. */}
                  <Bar className="w-full aspect-[4/3] rounded-xl mb-4" />

                  {/* Name, location, meta */}
                  <div className="flex flex-col">
                    <Bar className="h-7 w-4/5" />
                    <Bar className="h-4 w-3/5 mt-3" />
                    <Bar className="h-4 w-2/5 mt-2" />
                  </div>

                  <div className="flex flex-col mt-2">
                    <StatsRowSkeleton />
                    {/* Contribute information */}
                    <Bar className="h-10 w-full rounded-lg mt-6" />
                    {/* Claim this business */}
                    <Bar className="h-4 w-2/3 mt-3 mx-auto" />
                  </div>
                </div>
              </div>

              {/* ===== RIGHT COLUMN (all viewports) ===== */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Mobile header block - the left column's content, stacked. */}
                <div className="lg:hidden w-full flex flex-col gap-2">
                  <Bar className="w-full aspect-[4/3] rounded-xl" />
                  <div className="mt-4">
                    <StatsRowSkeleton />
                  </div>
                  <Bar className="h-11 w-full rounded-lg mt-4 mb-2" />
                  <Bar className="h-4 w-1/2 mx-auto" />
                </div>

                {/* Tab bar */}
                <div className="flex h-14 items-center gap-8 border-b border-border-token-brand-minimal shrink-0 px-4 lg:px-6">
                  {["w-24", "w-16", "w-14", "w-24", "w-14"].map((width) => (
                    <Bar key={width} className={cn("h-5 shrink-0", width)} />
                  ))}
                </div>

                {/* Tab panel */}
                <div className="flex-1 flex flex-col w-full lg:min-h-0 lg:mt-4">
                  <div className="flex-1 overflow-hidden md:border md:border-border-token-brand-minimal md:rounded-xl">
                    <div className="flex flex-col md:p-4 pt-4">
                      {/* Accessibility summary card */}
                      <Bar className="h-[112px] w-full rounded-xl mb-4" />
                      {Array.from({ length: 4 }, (_, index) => (
                        <PanelRowSkeleton key={index} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map column */}
        <div className="min-w-0 hidden md:block mt-4 mr-4 mb-4">
          <Bar className="h-full w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
};

export default VenueDetailsSkeleton;
